import * as React from 'react';

import { useComponentsContext } from './BlocksRenderer';

interface TextInlineNode {
  type: 'text';
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
}

type Modifier = Exclude<keyof TextInlineNode, 'type' | 'text'>;

type TextInlineProps = Omit<TextInlineNode, 'type'>;

const replaceLineBreaks = (text: string) => {
  const split = text.split(/\r?\n|\r/g);
  return (
    <>
      {split.map((part, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <br />}
          {part}
        </React.Fragment>
      ))}
    </>
  );
};

const Text = ({ text, ...modifiers }: TextInlineProps) => {
  // Get matching React component from the context
  const { modifiers: modifierComponents, missingModifierTypes } = useComponentsContext();

  const modifierNames = Object.keys(modifiers) as Modifier[];

  // Loop on each active modifier to wrap the text in its component
  return modifierNames.reduce(
    (children, modifierName) => {
      // Don't wrap the text if the modifier is disabled
      if (!modifiers[modifierName]) {
        return children;
      }

      const ModifierComponent = modifierComponents[modifierName];

      if (!ModifierComponent) {
        // Only warn once per missing modifier
        if (!missingModifierTypes.includes(modifierName)) {
          console.warn(
            `[@strapi/block-react-renderer] No component found for modifier "${modifierName}"`
          );
          missingModifierTypes.push(modifierName);
        }

        // Don't throw an error, just ignore the modifier
        return children;
      }

      return <ModifierComponent>{children}</ModifierComponent>;
    },
    // By default, return the text without any wrapper to avoid useless nesting
    replaceLineBreaks(text)
  );
};

export type { TextInlineNode, Modifier };
export { Text };